import { createContext } from "react";

import { useCallback, useMemo, useRef, useState } from "react";
import { ItemType, TrackType } from "../types";
import { debounce } from "../utils/debounce";
import { getCompositionDuration } from "../utils/get-composition-duration";
import { generateRandomId } from "./utils";

export interface TimelineInitialState {
  tracks: TrackType[];
  fps: number;
}

interface UseTimelineProps {
  initialState: TimelineInitialState;
  onChange?: (changedState: { tracks: TrackType[] }) => void;
}

// How often the onChange callback is called
const DEBOUNCE_TIME = 300;

interface TimelineState {
  tracks: TrackType[];
  fps: number;
  timelineRef: React.RefObject<HTMLDivElement>;
  timelineScrollableContainerRef: React.RefObject<HTMLDivElement>;
  selectedItem: string | null;
  durationInFrames: number;
  setTracks: (
    tracks: TrackType[] | ((prev: TrackType[]) => TrackType[])
  ) => void;
  setTrack: (idx: number, newTrack: TrackType) => void;
  addTrack: (track: TrackType) => void;
  deleteTrack: (trackId: string) => void;
  addItem: (trackIndex: number, item: ItemType) => void;
  changeItem: (itemId: string, updater: (item: ItemType) => ItemType) => void;
  deleteItem: (id: string) => void;
  findSpaceForItem: (
    durationInFrames: number,
    startAt: number
  ) => {
    trackIndex: number;
    startAt: number;
  } | null;
  ensureAddAndSelectItem: (trackIndex: number, item: ItemType) => void;
  setSelectedItem: (itemId: string | null) => void;
}

export const TimelineContext = createContext<TimelineState | null>(null);

type TimelineProviderProps = {
  children: React.ReactNode;
} & UseTimelineProps;

/**
 * This is the provider for the timeline.
 * It's here to show you how to initialize the timeline with data.
 * You can change the shape of the data to fit your needs.
 * @param children - The children of the provider. Pass the component containing your TimelineRoot and VideoPreview.
 * @param initialState - The initial state of the timeline.
 * @param onChange - The callback for changes in the timeline data. Use it store the data in your database.
 */
export const TimelineProvider = ({
  children,
  initialState,
  onChange,
}: TimelineProviderProps) => {
  const timelineRef = useRef<HTMLDivElement>(null);
  const timelineScrollableContainerRef = useRef<HTMLDivElement>(null);

  const [tracks, setTracks] = useState<TrackType[]>(initialState.tracks);
  const [selectedItem, setSelectedItem] = useState<string | null>(null);
  const [fps] = useState(initialState.fps);
  const debouncedOnChange = useMemo(
    () =>
      onChange ? debounce<typeof onChange>(onChange, DEBOUNCE_TIME) : undefined,
    [onChange]
  );

  const updateTracks = useCallback(
    (newTracks: TrackType[] | ((prev: TrackType[]) => TrackType[])) => {
      setTracks((prev) => {
        const nextTracks =
          typeof newTracks === "function" ? newTracks(prev) : newTracks;
        debouncedOnChange?.({
          tracks: nextTracks,
        });
        return nextTracks;
      });
    },
    [debouncedOnChange]
  );

  const setTrack = useCallback(
    (idx: number, newTrack: TrackType) => {
      updateTracks((tracks) => {
        return tracks.map((existingTrack, trackIndex) => {
          if (trackIndex === idx) {
            return newTrack;
          }
          return existingTrack;
        });
      });
    },
    [updateTracks]
  );

  const addTrack = useCallback(
    (track: TrackType) => {
      updateTracks((tracks) => [track, ...tracks]);
    },
    [updateTracks]
  );

  const deleteTrack = useCallback(
    (trackId: string) => {
      updateTracks((tracks) => tracks.filter((track) => track.id !== trackId));
    },
    [updateTracks]
  );

  const addItem = useCallback((trackIndex: number, item: ItemType) => {
    setTracks((tracks) => {
      return tracks.map((track, idx) => {
        if (idx === trackIndex) {
          return { ...track, items: [...track.items, item] };
        }
        return track;
      });
    });
  }, []);

  const changeItem = useCallback(
    (itemId: string, updater: (item: ItemType) => ItemType) => {
      setTracks((tracks) => {
        return tracks.map((track) => {
          return {
            ...track,
            items: track.items.map((existingItem) => {
              if (existingItem.id !== itemId) {
                return existingItem;
              }

              return updater(existingItem);
            }),
          };
        });
      });
    },
    []
  );

  const deleteItem = useCallback((id: string) => {
    setTracks((tracks) =>
      tracks.map((track) => {
        return {
          ...track,
          items: track.items.filter((_item) => {
            if (_item.id !== id) {
              return true;
            }

            return false;
          }),
        };
      })
    );
  }, []);

  const findSpaceForItem = useCallback(
    (durationInFrames: number, startAt: number) => {
      for (let trackIndex = 0; trackIndex < tracks.length; trackIndex++) {
        const track = tracks[trackIndex];
        const isBusy = track.items.find((item) => {
          return (
            (item.from <= startAt &&
              item.from + item.durationInFrames > startAt) ||
            (item.from > startAt && item.from < startAt + durationInFrames)
          );
        });

        if (isBusy) {
          continue;
        }

        return { trackIndex, startAt };
      }

      return {
        trackIndex: tracks.length,
        startAt,
      };
    },
    [tracks]
  );

  const ensureAddAndSelectItem = useCallback(
    (trackIndex: number, item: ItemType) => {
      if (trackIndex === tracks.length) {
        addTrack({
          id: generateRandomId(),
          name: `Track ${tracks.length + 1}`,
          items: [],
        });
      }

      addItem(trackIndex, item);
      setSelectedItem(item.id);
    },
    [addItem, addTrack, tracks.length]
  );

  const isItemDragging = useMemo(() => {
    const items = tracks.flatMap((t) => t.items);
    return items.some((i) => "isDragging" in i && i.isDragging);
  }, [tracks]);

  const durationInFrames = useMemo(() => {
    const framesShown = Math.max(1, getCompositionDuration(tracks));
    return framesShown;
  }, [tracks]);

  const lastDurationWhileNotDragging = useRef(durationInFrames);
  if (!isItemDragging) {
    lastDurationWhileNotDragging.current = durationInFrames;
  }

  const state: TimelineState = useMemo(
    () => ({
      tracks,
      setTracks: updateTracks,
      setTrack,
      addTrack,
      deleteTrack,
      addItem,
      changeItem,
      timelineRef,
      timelineScrollableContainerRef,
      deleteItem,
      findSpaceForItem,
      setSelectedItem,
      selectedItem,
      ensureAddAndSelectItem,
      durationInFrames: lastDurationWhileNotDragging.current,
      fps,
    }),
    [
      tracks,
      updateTracks,
      setTrack,
      addTrack,
      deleteTrack,
      selectedItem,
      changeItem,
      addItem,
      deleteItem,
      findSpaceForItem,
      ensureAddAndSelectItem,
      fps,
    ]
  );

  return (
    <TimelineContext.Provider value={state}>
      {children}
    </TimelineContext.Provider>
  );
};
